<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Beneficiaries"
      text="Update beneficiaries"
      class="mb-0"
      :loading="loading"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="localBeneficiary.name"
                v-validate="'required|max:80|nameAllowedFormat'"
                label="Name"
                :error-messages="errors.collect('name')"
                data-vv-name="name"  
                outlined
                :class="{
                  'required':!localBeneficiary.name
                }"
                @input="localBeneficiary.name = localBeneficiary.name.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <span> Citizenship? </span>
              <v-radio-group
                v-model="isCitizen"
                class="mt-0"
                row
                @change="localBeneficiary.ic=null,localBeneficiary.passport_no=null"
              >
                <v-radio
                  label="Non-citizen"
                  :value="0"
                />
                <v-radio
                  label="Citizen"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-text-field 
                v-show="isCitizen === 1"
                v-model="localBeneficiary.ic"
                v-validate="isCitizen === 1 ? 'required|icDate|icLength' : ''"
                v-mask="'######-##-####'"
                label="IC"
                :error-messages="errors.collect('ic')"
                data-vv-name="ic"
                data-vv-as="IC"
                outlined
                :class="{
                  'required':!localBeneficiary.ic && isCitizen === 1
                }"
              />
              <v-text-field
                v-show="isCitizen === 0"
                v-model="localBeneficiary.passport_no"
                v-validate="isCitizen === 0 ? 'required' : ''"
                label="Passport No."
                :error-messages="errors.collect('passport_no')"
                data-vv-name="passport_no"
                data-vv-as="passport number"
                outlined
                :class="{
                  'required':!localBeneficiary.passport_no && isCitizen === 0
                }"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localBeneficiary.relationship_id"
                v-validate="'required'"
                :items="relationships"
                label="Relationship"
                :error-messages="errors.collect('relationship_id')"
                data-vv-name="relationship_id"    
                data-vv-as="relationship"     
                item-text="name"
                item-value="id"
                outlined
                :class="{
                  'required':!localBeneficiary.relationship_id
                }"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-text-field
                v-model="localBeneficiary.percentage_of_share"
                v-validate="'required|min_value:1|max_value:100|totalShareNotExceed'"
                :error-messages="errors.collect('percentageOfShare')"
                data-vv-name="percentageOfShare"                
                data-vv-as="percentage of share"                
                label="% of Share"
                outlined
                type="number"
                min="1"
                max="100"
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card> 
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      beneficiary: {
        type: Object,
        default: null,
      },
      beneficiaries: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localBeneficiary: null,
        relationships: [],
        loading: false,
        isCitizen: 1,
      }
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        
        this.$validator.extend('totalShareNotExceed', {
          getMessage () {
            return 'Total percentage of share cannot exceed 100%'
          },
          validate: (value) => {
            const num = Number(value || 0)
            const existing = this.beneficiaries.reduce(
              (sum, b) => sum + Number(b.percentage_of_share || 0),
              0
            )
            return existing - Number(this.beneficiary.percentage_of_share) + num <= 100
          },
        })
        
        this.$validator.extend('icDate', {
          getMessage (field, args) {
            return 'Invalid IC Date'        
          },
          validate (value, args) {
            var y = parseInt(value.substr(0, 2))
            var m = parseInt(value.substr(2, 2))
            var d = parseInt(value.substr(4, 2))
            if (m === 0 || m > 12) { return false }
            var monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ]
            // Adjust for leap years
            if (y % 400 === 0 || (y % 100 !== 0 && y % 4 === 0)) { monthLength[1] = 29 }
            return d > 0 && d <= monthLength[m - 1]
          },
        })

        this.$validator.extend('icLength', {
          getMessage (field, args) {
            return 'IC not in right length'        
          },
          validate (value, args) {
            return value.toString().length === 14
          },
        })

        this.$validator.extend('nameAllowedFormat', {
          getMessage (field, args) {
            return "Invalid name format. Only alphabet and special character of @ ' * - / . allowed for name"     
          },
          validate (value, args) {
            const regex = /^[ A-Za-z'@./*-]*$/
            return regex.test(value)
          },
        })

        this.loading = true

        this.localBeneficiary = { ...this.beneficiary }
        this.isCitizen = this.localBeneficiary.ic ? 1 : 0

        this.$api.hrisRelationships().list()
          .then(res => {
            this.relationships = res.data

            this.loading = false
          })
      },
    },
    methods: { 
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)

            this.$api.beneficiaries().update(this.beneficiary.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.setProgressing(false)
                  this.dialog = false
                }
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        return {
          name: this.localBeneficiary.name.toUpperCase(),
          relationship_id: this.localBeneficiary.relationship_id,
          is_citizen: this.isCitizen,
          ic: this.localBeneficiary.ic ? this.localBeneficiary.ic.replace(/-/g, '') : null,
          passport_no: this.localBeneficiary.passport_no ? this.localBeneficiary.passport_no : null,
          percentage_of_share: this.localBeneficiary.percentage_of_share,
        }
      },
    },
  }
</script>

<style scoped>
.required >>> fieldset {
  border: 2px solid red;
}
</style>
